/**
 * ImageGalleryModal Component
 *
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 *
 * Purpose:
 * Full-screen image gallery modal for property photos with navigation, thumbnails, and mobile booking bar.
 * Provides immersive image viewing experience with keyboard navigation and touch support.
 *
 * Features:
 * - Full-screen modal with black background
 * - Main image display with smooth transitions
 * - Previous/Next navigation arrows (disabled at boundaries)
 * - Image counter (e.g., "3 / 12 photos")
 * - Scrollable thumbnail strip (desktop only)
 * - Mobile booking bar with price and CTA (mobile only)
 * - Keyboard navigation (Escape, ArrowLeft, ArrowRight)
 * - Background click to close
 * - Close button with label
 * - Smooth thumbnail auto-scroll to active image
 * - Event propagation control to prevent unintended closes
 * - Scroll position management (saves and restores scroll position)
 *
 * Props:
 * - images: string[] (array of image URLs)
 * - isOpen: boolean (modal visibility)
 * - currentIndex: number (currently displayed image index)
 * - onClose: () => void (close handler)
 * - onPrevious: () => void (previous image handler)
 * - onNext: () => void (next image handler)
 * - onSelectImage: (index: number) => void (thumbnail click handler)
 * - propertyTitle?: string (optional property name)
 * - propertyLocation?: string (optional property location)
 * - propertyPrice?: number (optional price for mobile booking bar)
 * - onBookNow?: () => void (optional booking handler)
 *
 * Dependencies:
 * - React 19.2.4 (hooks, refs)
 * - Framer Motion 12 (animations, AnimatePresence)
 * - Font Awesome 6.5 (icons)
 * - TailwindCSS 4.1 (responsive styling)
 *
 * Responsive Behavior:
 * - Desktop (sm and up): Shows thumbnail strip at bottom
 * - Mobile (below sm): Hides thumbnails, shows booking bar instead
 * - Booking button: Desktop in top bar, Mobile in bottom bar
 *
 * Keyboard Shortcuts:
 * - Escape: Close modal
 * - ArrowLeft: Previous image
 * - ArrowRight: Next image
 *
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with mobile booking bar
 * - Added mobile-specific booking bar (hidden on desktop)
 * - Desktop booking button in top bar only
 * - Thumbnail strip hidden on mobile to make room for booking bar
 * - Fixed event propagation for proper modal close behavior
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProIcon } from './ProIcon';
import { StickyBookBar } from './StickyBookBar';
import { useModal } from '../contexts/ModalContext';

interface ImageGalleryModalProps {
  images: string[];
  isOpen: boolean;
  currentIndex: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onSelectImage: (index: number) => void;
  propertyTitle?: string;
  propertyLocation?: string;
  propertyPrice?: number;
  onBookNow?: () => void;
  totalPrice?: number;
  nights?: number;
  rating?: number;
  reviewCount?: number;
}

export const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
  images,
  isOpen,
  currentIndex,
  onClose,
  onPrevious,
  onNext,
  onSelectImage,
  propertyTitle,
  propertyLocation,
  propertyPrice,
  onBookNow,
  totalPrice,
  nights,
  rating = 4.8,
  reviewCount = 127
}) => {
  const { openModal, closeModal } = useModal();
  const thumbStripRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  
  // Touch state for swipe detection
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  // Save scroll position when modal opens
  useEffect(() => {
    if (isOpen) {
      openModal('image-gallery');
      scrollPositionRef.current = window.scrollY;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      closeModal('image-gallery');
      // Restore body scroll
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      closeModal('image-gallery');
    };
  }, [isOpen]); // Remove openModal and closeModal from dependencies

  // Restore scroll position when modal closes
  useEffect(() => {
    if (!isOpen && scrollPositionRef.current > 0) {
      // Use requestAnimationFrame to ensure smooth restoration
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPositionRef.current);
        scrollPositionRef.current = 0;
      });
    }
  }, [isOpen]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < images.length - 1) {
      onNext();
    }
    if (isRightSwipe && currentIndex > 0) {
      onPrevious();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          onNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [isOpen, onClose, onPrevious, onNext]);

  // Removed auto-scroll effect since thumbnails are now fixed without overflow

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-chat-blocker="booking-cta"
      className="fixed inset-0 z-[9999] bg-white flex flex-col"
      onClick={onClose}
    >
      {/* Top Bar: Back + Close + Counter */}
      <div className="flex items-center justify-between px-4 py-4 flex-shrink-0 bg-white z-20" onClick={(e) => e.stopPropagation()}>
          {/* Left: Back button - Desktop only */}
          <button
            onClick={onClose}
            className="hidden sm:flex items-center gap-2 text-black/80 hover:text-black transition-colors text-sm font-medium"
          >
            <ProIcon icon="chevron-left" family="light" className="w-5 h-5" />
            <span>Back</span>
          </button>

          {/* Center: Counter (if multiple images) */}
          {images.length > 1 && (
            <span className="text-black/70 text-sm font-medium absolute left-1/2 -translate-x-1/2">
              {currentIndex + 1} / {images.length}
            </span>
          )}

          {/* Right: Close button - Desktop only */}
          <div className="flex items-center gap-3">
            {/* Desktop booking button */}
            {onBookNow && propertyPrice && (
              <button
                onClick={(e) => { e.stopPropagation(); onBookNow(); }}
                className="ui-btn-primary px-5 py-2 rounded-lg text-sm whitespace-nowrap"
              >
                From €{propertyPrice} · BOOK
              </button>
            )}

            {/* Close button - desktop only */}
            <button
              onClick={onClose}
              className="hidden sm:flex items-center justify-center w-10 h-10 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all"
              aria-label="Close gallery"
            >
              <ProIcon icon="xmark" family="light" className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Image Area with Swipe Support */}
        <div 
          className="flex-1 flex items-center justify-center relative min-h-0 px-4 z-10 touch-pan-y"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close hint - moved to top of header area, not over image */}
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-black/40 text-xs pointer-events-none whitespace-nowrap">
            Press ESC to close
          </div>

          {/* Close button - top right corner, always visible */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-30 flex items-center justify-center w-10 h-10 bg-white/90 hover:bg-white text-slate-800 rounded-full transition-all shadow-lg border border-slate-200"
            aria-label="Close gallery"
          >
            <ProIcon icon="xmark" family="light" className="w-5 h-5" />
          </button>

          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); onPrevious(); }}
              disabled={currentIndex === 0}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors disabled:opacity-20 disabled:cursor-not-allowed shadow-lg border border-slate-200"
            >
              <ProIcon icon="chevron-left" family="light" className="w-5 h-5 text-slate-800" />
            </button>
          )}

          <motion.img
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            src={images[currentIndex]}
            alt={`Property image ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            draggable={false}
          />

          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              disabled={currentIndex === images.length - 1}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors disabled:opacity-20 disabled:cursor-not-allowed shadow-lg border border-slate-200"
            >
              <ProIcon icon="chevron-right" family="light" className="w-5 h-5 text-slate-800" />
            </button>
          )}
        </div>

        {/* Thumbnail Strip - Desktop: visible, Mobile: hidden */}
        {images.length > 1 && (
          <div className="flex-shrink-0 py-4 px-4 pb-20 hidden md:block bg-white" onClick={(e) => e.stopPropagation()}>
            <div
              ref={thumbStripRef}
              className="flex gap-2 overflow-hidden justify-center"
            >
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => onSelectImage(index)}
                  className={`flex-shrink-0 rounded-lg overflow-hidden transition-all duration-200 ${
                    index === currentIndex
                      ? 'ring-2 ring-slate-400 opacity-100 scale-105'
                      : 'opacity-40 hover:opacity-70'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Photo ${index + 1}`}
                    className="w-16 h-12 sm:w-20 sm:h-14 object-cover"
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sticky Booking Bar - Using Shared Component */}
        {onBookNow && propertyPrice && (
          <StickyBookBar
            mode="gallery"
            isVisible={true}
            price={propertyPrice}
            rating={rating}
            reviewCount={reviewCount}
            title={propertyTitle}
            location={propertyLocation}
            totalPrice={totalPrice}
            nights={nights}
            onBookNow={onBookNow}
          />
        )}
      </motion.div>
  );
};
